/**
 * OMON-173: persist the librarian-set within-bucket fact order (drag-to-reorder).
 *
 * The catalogue tree sorts facts within a bucket (category + optional
 * sub-category) by `ObservationDefinition._order` (see tree.ts). This module
 * writes that field when a librarian drops a fact at a new position. Mirrors the
 * existing category/sub-category ordering pattern (hierarchy.ts swap*Order), one
 * layer down. Order lives on the OD only (a catalogue-display concern); the AD
 * doesn't carry it.
 */

import { doc, writeBatch } from "firebase/firestore";

import { db } from "../firebase";
import { state } from "../state";
import { statusEl } from "../dom";

/**
 * Renumber a bucket: assign `_order` = position (0..N-1) to each fact in
 * `orderedNames`, persisting only the ones whose order actually changed. Mirrors
 * the cache AFTER a successful commit (so a failed write never leaves the cache
 * ahead of Firestore — feedback_silent_success_stale_cache). Returns true on
 * success or no-op, false on a write error (surfaced in statusEl).
 */
export async function reorderFactsInBucket(orderedNames: string[]): Promise<boolean> {
  const updates: Array<{ name: string; order: number }> = [];
  orderedNames.forEach((name, idx) => {
    const od = state.catalogueData.get(name);
    if (od && od._order !== idx) updates.push({ name, order: idx });
  });
  if (updates.length === 0) return true;
  try {
    const batch = writeBatch(db);
    for (const u of updates) {
      batch.update(doc(db, "_observationDefinition", u.name), { _order: u.order });
    }
    await batch.commit();
    for (const u of updates) {
      const od = state.catalogueData.get(u.name);
      if (od) od._order = u.order;
    }
    return true;
  } catch (err) {
    console.error("Failed to persist fact order:", err);
    statusEl.textContent = "Error saving order — check console";
    return false;
  }
}
